import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { KeyRound, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { useAuth } from '../../common/context/AuthContext';
import axios from 'axios';
import Toast from '../../common/components/Toast';
import { API_URL } from '../../api/config';

export default function PinLogin() {
  const [pinInput, setPinInput] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn: authSignIn } = useAuth();
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const email = location.state?.email || '';
  const restaurantId = location.state?.restaurantId || '';
  const restaurantName = location.state?.restaurantName || '';
  const outletName = location.state?.outletName || '';

  useEffect(() => {
    if (!email || !restaurantId) {
      navigate('/signin');
    }
  }, [email, restaurantId, navigate]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const closeToast = () => {
    setToast({ ...toast, show: false });
  };

  const handlePinInputChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newPin = [...pinInput];
    newPin[index] = value.slice(-1);
    setPinInput(newPin);
    setError('');

    // Auto-focus next input
    if (value && index < 3) {
      const nextInput = document.getElementById(`pin-${index + 1}`);
      if (nextInput) nextInput.focus();
    }

    // Auto-submit when all digits are entered
    if (index === 3 && value && newPin.every(d => d)) {
      handlePinLogin(newPin.join(''));
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      const prevInput = document.getElementById(`pin-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handlePinLogin = async (pin = null) => {
    const pinCode = pin || pinInput.join('');
    
    if (pinCode.length !== 4) {
      setError('Please enter a 4-digit PIN');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/auth/pin-login`, {
        email,
        restaurantId,
        pin: pinCode
      });

      if (response.data.success) {
        const { token, user } = response.data;
        await authSignIn({ token, user });
        showToast('Signed in successfully');
        setTimeout(() => navigate('/dashboard'), 500);
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Invalid PIN');
      setPinInput(['', '', '', '']);
      document.getElementById('pin-0')?.focus();
      showToast(error.response?.data?.message || 'Invalid PIN', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUsePassword = () => {
    if (location.state?.outlets) {
      navigate('/outlet-selection', { 
        state: { 
          outlets: location.state.outlets, 
          email 
        } 
      });
    } else {
      navigate('/signin');
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-gray-950">
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}
      
      {/* Header */}
      <header className="flex items-center justify-between px-6 md:px-12 py-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-100">TOBO</h2>
          <span className="text-sm font-semibold text-blue-700 dark:text-blue-200">BILL IT, DONE!</span>
        </div>
        <button 
          onClick={() => navigate('/signin')}
          className="text-gray-600 dark:text-gray-400 text-sm font-medium hover:text-blue-600 dark:hover:text-blue-300 transition-colors"
        >
          Support
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-full max-w-[440px] bg-white dark:bg-gray-900 rounded-3xl border border-gray-200 dark:border-gray-800 p-8 md:p-12 shadow-xl">
          {/* Back Button */}
          <button
            onClick={handleUsePassword}
            className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors mb-6"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Use Password Instead</span>
          </button>

          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <KeyRound className="w-10 h-10 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-4xl font-black text-gray-900 dark:text-white mb-2">Enter PIN</h1>
            <p className="text-base text-gray-600 dark:text-gray-400 mb-1">{restaurantName}</p>
            {outletName && (
              <p className="text-sm text-gray-500 dark:text-gray-500">{outletName}</p>
            )}
          </div>

          <div className="space-y-6">
            {/* PIN Input */}
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-gray-900 dark:text-gray-200 text-center">
                Enter your 4-digit PIN
              </label>
              <div className="flex justify-center gap-3">
                {pinInput.map((digit, index) => (
                  <input
                    key={index}
                    id={`pin-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handlePinInputChange(index, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(index, e)}
                    disabled={loading}
                    className={`w-16 h-16 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                      error 
                        ? 'border-red-500 focus:ring-red-500 focus:border-red-500 dark:border-red-500' 
                        : 'border-gray-300 dark:border-gray-700 focus:ring-blue-600 focus:border-blue-600'
                    } bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed`}
                    autoFocus={index === 0}
                  />
                ))}
              </div>
              {error && (
                <div className="flex items-center justify-center gap-2 text-red-600 dark:text-red-400">
                  <AlertCircle size={16} />
                  <p className="text-sm font-medium">{error}</p>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              onClick={() => handlePinLogin()}
              disabled={loading || pinInput.some(d => !d)}
              className="w-full h-14 rounded-2xl bg-blue-600 text-white font-bold text-base shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40 hover:bg-blue-700 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 transition-all mt-4"
            >
              {loading ? 'Verifying...' : 'Sign In'}
            </button>
          </div>

          {/* Forgot PIN */}
          <div className="mt-8 text-center">
            <button 
              onClick={() => navigate('/forgot-password', { state: { email, restaurantId } })}
              className="text-blue-600 dark:text-blue-400 text-sm font-semibold hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            >
              Forgot PIN?
            </button>
          </div>

          {/* Trust Indicators */}
          <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-800 flex items-center justify-center gap-6 opacity-70">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-xs font-medium text-gray-600 dark:text-gray-500">Secure Login</span>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <p className="text-xs text-gray-600 dark:text-gray-500 uppercase font-semibold">
          © 2024 TOBO Inc. • All Rights Reserved
        </p>
      </footer>
    </div>
  );
}